"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useOptimisticSelection } from "../../hooks/index.js";
import { cn } from "../../utils/index.js";
import { RadioChipGroup } from "../Radio/RadioChipGroup/RadioChipGroup.js";
import { segmentedChipGroupVariants } from "./SegmentedChipGroup.variants.js";
function SegmentedChipGroup({ name, defaultValue, value, handleChange, className, trackClassName, fullWidth = false, options, disabled, required, ...props }) {
    const { trackSlot, shellSlot, groupSlot, indicatorSlot, chipOnTrackSlot } = segmentedChipGroupVariants({
        fullWidth
    });
    const resolvedDefaultValue = defaultValue ?? (options[0] ? String(options[0].value) : void 0);
    const isControlled = void 0 !== value;
    const resolvedControlledValue = isControlled ? String(value) : resolvedDefaultValue;
    const [selectedValue, setSelectedValue] = useOptimisticSelection(resolvedControlledValue, {
        isControlled,
        initial: resolvedControlledValue
    });
    const shellRef = useRef(null);
    const [indicatorRect, setIndicatorRect] = useState(null);
    const handleValueChange = useCallback((newValue, event)=>{
        setSelectedValue(String(newValue));
        handleChange(newValue, event);
    }, [
        handleChange,
        setSelectedValue
    ]);
    const activeValue = selectedValue;
    const elevatedOptions = useMemo(()=>options.map((option)=>({
                ...option,
                type: option.type ?? 'chip',
                emphasis: option.emphasis ?? 'regular',
                showCheckIcon: false,
                elevated: false,
                className: cn(chipOnTrackSlot(), option.className)
            })), [
        chipOnTrackSlot,
        options
    ]);
    useLayoutEffect(()=>{
        const shell = shellRef.current;
        if (!shell) return;
        const measure = ()=>{
            const radioGroup = shell.querySelector('[role="radiogroup"]');
            if (!radioGroup) return void setIndicatorRect(null);
            const buttons = Array.from(radioGroup.querySelectorAll(':scope > button'));
            const idx = options.findIndex((o)=>String(o.value) === activeValue);
            if (idx < 0 || idx >= buttons.length || !buttons[idx]) return void setIndicatorRect(null);
            const shellRect = shell.getBoundingClientRect();
            const btnRect = buttons[idx].getBoundingClientRect();
            const ancestorScale = shell.offsetWidth > 0 ? shellRect.width / shell.offsetWidth : 1;
            setIndicatorRect({
                top: (btnRect.top - shellRect.top) / ancestorScale,
                left: (btnRect.left - shellRect.left) / ancestorScale,
                width: btnRect.width / ancestorScale,
                height: btnRect.height / ancestorScale
            });
        };
        const rafMeasure = ()=>{
            window.requestAnimationFrame(measure);
        };
        measure();
        const ro = new ResizeObserver(rafMeasure);
        ro.observe(shell);
        const radioGroup = shell.querySelector('[role="radiogroup"]');
        if (radioGroup) ro.observe(radioGroup);
        const fonts = document.fonts;
        if (fonts?.ready) fonts.ready.then(rafMeasure);
        window.addEventListener('resize', rafMeasure);
        return ()=>{
            ro.disconnect();
            window.removeEventListener('resize', rafMeasure);
        };
    }, [
        activeValue,
        options
    ]);
    return /*#__PURE__*/ jsx("div", {
        className: cn(trackSlot(), trackClassName),
        children: /*#__PURE__*/ jsxs("div", {
            ref: shellRef,
            className: shellSlot(),
            children: [
                null !== indicatorRect && indicatorRect.width > 0 && indicatorRect.height > 0 ? /*#__PURE__*/ jsx("div", {
                    "aria-hidden": true,
                    className: indicatorSlot(),
                    style: {
                        top: indicatorRect.top,
                        left: indicatorRect.left,
                        width: indicatorRect.width,
                        height: indicatorRect.height
                    }
                }) : null,
                /*#__PURE__*/ jsx(RadioChipGroup, {
                    ...props,
                    name: name,
                    defaultValue: void 0 === value ? resolvedDefaultValue : defaultValue,
                    value: value,
                    handleChange: handleValueChange,
                    options: elevatedOptions,
                    disabled: disabled,
                    required: required,
                    layout: "horizontal",
                    className: cn(groupSlot(), className)
                })
            ]
        })
    });
}
export { SegmentedChipGroup };
